import {first} from 'lodash';
import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {searchableAttrsFrom} from '../../../../../../../srcCommon/inboxes/search/searchAttributesHelpers';
import {reportToBugsnag} from '../../../../../../../srcCommon/services/bugsnag/bugsnag';
import {buildTeammateDisplayName} from '../../../../../../helpers/modelHelpers';
import {
  buildContactDisplayHandle,
  buildContactDisplayName,
} from '../../../../../../helpers/models/contactHelpers';
import {VisualRecipientMap} from '../../../../../../helpers/models/recipientHelpers';
import {ActivitySourceVisualTypesEnum} from '../../../../../../models/api/activitySourceModel';
import {MessageErrorsEnum, MessageSecurityErrorsEnum} from '../../../../../../models/api/messageErrorModel';
import {MessageModel} from '../../../../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../../../../models/api/recipientModel';
import {TeammateModel} from '../../../../../../models/api/teammateModel';
import {VisualRecipientModel} from '../../../../../../models/api/visualRecipientModel';
import {
  computeAutoResponsePillText,
  computeIsAutoResponseMessageActivitySource,
} from '../../../../../../store/data/activities/autoResponseMessageActivityHelpers';
import {
  AccountDetailsPanelId,
  ContactDetailsPanelId,
  EmlPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../store/panels/panelStateBase';
import {handleRenderers} from '../../../conversation/viewerConstants';
import {MessageViewerRecipientContainer} from '../messageViewerRecipientContainer';
import {MessageViewerUnread, MessageViewerUnreadProps} from './messageViewerUnread';

/*
 * Props.
 */

export interface MessageViewerSenderProps extends MessageViewerUnreadProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId | ContactDetailsPanelId | AccountDetailsPanelId;
  searchQuery?: string;
  message: MessageModel;
  author?: TeammateModel;
  widgetCompanyName?: string;
  visualRecipients: VisualRecipientMap;
  activitySourceVisualType: ActivitySourceVisualTypesEnum | undefined;
  isMessageViewerExpanded: boolean;
  isClickable?: boolean;
  needsParentHover?: boolean;
  shouldHideClickableUntilHover?: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledUnverifiedDiv = styled.div`
  margin-right: 4px;
`;

const StyledSenderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
  line-height: 20px;
  ${ellipsis()};
`;

const StyledRecipientSpan = styled.span`
  color: ${(p) => p.theme.greys.shade80};
`;

const StyledNameSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
  font-weight: ${fontWeights.semibold};
`;

export const StyledAutoResponsePillDiv = styled.div`
  margin: 0 0 0 5px;
  padding: 3.5px 5px;
  white-space: nowrap;
  background-color: ${(p) => p.theme.greys.shade40};
  color: ${(p) => p.theme.greys.shade80};
  border-radius: 100px;
  line-height: 9px;
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.bold};
`;

/*
 * Component.
 */

export const MessageViewerSender: FC<MessageViewerSenderProps> = (props) => {
  const {activitySourceVisualType} = props;
  // Never show a "NEW" unread indicator for auto-response messages.
  const isAutoResponse = computeIsAutoResponseMessageActivitySource(activitySourceVisualType);

  return (
    <StyledWrapperDiv>
      {maybeRenderUnverified(props)}
      <StyledSenderDiv {...searchableAttrsFrom}>{renderSender(props)}</StyledSenderDiv>
      {maybeRenderAutoResponsePill(props)}
      <MessageViewerUnread {...props} isAutoResponse={isAutoResponse} />
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderAutoResponsePill(props: MessageViewerSenderProps) {
  const text = computeAutoResponsePillText(props.activitySourceVisualType);
  return text ? <StyledAutoResponsePillDiv>{text}</StyledAutoResponsePillDiv> : null;
}

function maybeRenderUnverified(props: MessageViewerSenderProps) {
  if (!props.message.isSenderUnverified) {
    return null;
  }

  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledUnverifiedDiv>
          <Icon name="notificationSmall" color={theme.palette.purple.shade40} />
        </StyledUnverifiedDiv>
      )}
    </UserThemeConsumer>
  );
}

function renderSender(props: MessageViewerSenderProps) {
  const {author, visualRecipients, isClickable, panelId, searchQuery, widgetCompanyName} = props;
  const fromRecipient = first(visualRecipients[RecipientRolesEnum.FROM]);

  // If we have an author, use it.
  if (author) {
    return renderAuthor(author, fromRecipient, isClickable, widgetCompanyName);
  }

  // Otherwise, render the recipient.
  if (!fromRecipient) {
    const error = new Error(`A message with no "from" recipient was provided (${props.message.id})`);
    reportToBugsnag(error);

    return <Trans id="sender-missing">Unknown sender</Trans>;
  }

  const {securityError} = props.message;
  const shouldDisplayContactHandle = Boolean(
    securityError &&
      securityError.type === MessageErrorsEnum.PHISHING &&
      securityError.reason &&
      securityError.reason === MessageSecurityErrorsEnum.ID_USURPATION,
  );
  if (shouldDisplayContactHandle) {
    return <StyledNameSpan>{buildContactDisplayHandle(fromRecipient)}</StyledNameSpan>;
  }

  const {message, needsParentHover, shouldHideClickableUntilHover} = props;
  return (
    <StyledRecipientSpan>
      <MessageViewerRecipientContainer
        panelId={panelId}
        searchQuery={searchQuery}
        recipient={fromRecipient}
        isUnverified={message.isSenderUnverified}
        needsParentHover={needsParentHover}
        message={message}
        isBold
        searchableAttributes={searchableAttrsFrom}
        shouldHideClickableUntilHover={shouldHideClickableUntilHover}
      />
    </StyledRecipientSpan>
  );
}

function renderAuthor(
  author: TeammateModel,
  fromRecipient?: VisualRecipientModel,
  isClickable?: boolean,
  widgetCompanyName?: string,
) {
  const authorName = buildTeammateDisplayName(author);
  const authorNameRender = <StyledNameSpan>{authorName}</StyledNameSpan>;

  if (widgetCompanyName) {
    return (
      <>
        {authorNameRender} <Trans id="sender-as">as</Trans> &quot;{widgetCompanyName}&quot;
      </>
    );
  }

  if (!isClickable || !fromRecipient) {
    return authorNameRender;
  }

  const handleRenderer = handleRenderers[fromRecipient.contact.source];
  if (!handleRenderer) {
    return authorNameRender;
  }

  const recipientName = buildContactDisplayName(fromRecipient);
  const recipientHandle = buildContactDisplayHandle(fromRecipient);
  const recipientHandleRender = handleRenderer(recipientHandle);

  if (authorName === recipientName || recipientName === recipientHandle) {
    return (
      <>
        {authorNameRender} {recipientHandleRender}
      </>
    );
  }

  return (
    <Trans id="sender-authorrecipient">
      {authorNameRender} as &quot;{recipientName}&quot; {recipientHandleRender}
    </Trans>
  );
}
